/**
 * useTenantConfig Hook
 *
 * Custom hooks for tenant configuration management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Loest eine Datei-URL der API auf.
 *
 * Das Backend liefert Logo-/Favicon-URLs als API-relative Pfade
 * (`/uploads/logos/...`). Im Browser muessen sie an der API-Basis haengen,
 * sonst zeigen sie auf die Web-Domain und laufen ins Leere.
 */
export function resolveAssetUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//.test(url)) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
}

interface BrandingResponse {
  company_name: string;
  primary_color: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
}

interface TenantConfigResponse {
  id: string;
  company_name: string;
  primary_color: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  legal_name: string | null;
  vat_id: string | null;
  tax_number: string | null;
  registry_court: string | null;
  registry_number: string | null;
  managing_directors: string | null;
  quote_header: string | null;
  quote_footer: string | null;
  invoice_header: string | null;
  invoice_footer: string | null;
  email_signature: string | null;
  feature_flags: Record<string, boolean>;
  created_at: string;
  updated_at: string;
}

interface ContactInfoUpdate {
  email?: string;
  phone?: string;
  website?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
}

interface LegalInfoUpdate {
  legal_name?: string;
  vat_id?: string;
  tax_number?: string;
  registry_court?: string;
  registry_number?: string;
  managing_directors?: string;
}

interface TemplatesUpdate {
  quote_header?: string;
  quote_footer?: string;
  invoice_header?: string;
  invoice_footer?: string;
  email_signature?: string;
}

// Branding (public, no auth)
async function fetchBranding(): Promise<BrandingResponse> {
  const response = await fetch(`${API_BASE_URL}/config/branding`);
  if (!response.ok) {
    throw new Error('Fehler beim Laden des Brandings');
  }
  return response.json();
}

export function useBranding() {
  return useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// Full config (admin only)
async function fetchConfig(): Promise<TenantConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/config`, {
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin-Berechtigung erforderlich');
    }
    throw new Error('Fehler beim Laden der Konfiguration');
  }
  return response.json();
}

export function useTenantConfig(enabled = true) {
  return useQuery({
    queryKey: ['tenant-config'],
    queryFn: fetchConfig,
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
}

// Update branding (multipart form)
async function updateBranding(data: FormData): Promise<TenantConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/config/branding`, {
    method: 'PUT',
    credentials: 'include',
    body: data,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Fehler beim Speichern');
  }
  return response.json();
}

export function useUpdateBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBranding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
      queryClient.invalidateQueries({ queryKey: ['branding'] });
    },
  });
}

// Update contact
async function updateContact(data: ContactInfoUpdate): Promise<TenantConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/config/contact`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Fehler beim Speichern');
  }
  return response.json();
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
    },
  });
}

// Update legal
async function updateLegal(data: LegalInfoUpdate): Promise<TenantConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/config/legal`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Fehler beim Speichern');
  }
  return response.json();
}

export function useUpdateLegal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLegal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
    },
  });
}

// Update templates
async function updateTemplates(data: TemplatesUpdate): Promise<TenantConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/config/templates`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Fehler beim Speichern');
  }
  return response.json();
}

export function useUpdateTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTemplates,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
    },
  });
}

// Preview template
interface TemplatePreviewResponse {
  rendered: string;
  valid: boolean;
  error: string | null;
}

async function previewTemplate(template: string): Promise<TemplatePreviewResponse> {
  const response = await fetch(`${API_BASE_URL}/config/templates/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ template }),
  });
  if (!response.ok) {
    throw new Error('Fehler bei der Vorschau');
  }
  return response.json();
}

export function useTemplatePreview() {
  return useMutation({
    mutationFn: previewTemplate,
  });
}
